import "./fn";
import { quotes, type Quote } from "./api";
import { startAlertWatcher } from "./fn/alerts";
import { clear, h } from "./dom";
import * as f from "./fmt";
import { every, Panel, type PanelHost } from "./panel";

const STORE = "berg.v1";
const DEFAULTS = ["WEI", "TOP", "SPX Index GP", "W"];

interface Saved {
  layout: 1 | 2 | 4;
  focus: number;
  cmds: string[];
}

function loadSaved(): Saved {
  try {
    const s = JSON.parse(localStorage.getItem(STORE) ?? "") as Saved;
    if ([1, 2, 4].includes(s.layout) && Array.isArray(s.cmds)) return s;
  } catch {
    // first visit or corrupt
  }
  return { layout: 4, focus: 0, cmds: DEFAULTS };
}

class App implements PanelHost {
  panels: Panel[] = [];
  focused!: Panel;
  layout: 1 | 2 | 4;
  grid = h("div", { id: "grid" });
  layoutButtons = h("div", { class: "layout" });

  constructor() {
    const saved = loadSaved();
    this.layout = saved.layout;
    document.body.append(topbar(this), this.grid);
    for (let i = 0; i < 4; i++) {
      const p = new Panel(i, this);
      this.panels.push(p);
      this.grid.append(p.el);
    }
    this.setLayout(this.layout);
    this.focus(this.panels[Math.min(saved.focus, this.layout - 1)] ?? this.panels[0]);
    this.panels.forEach((p, i) => p.run(saved.cmds[i] || DEFAULTS[i], false));
    this.fromHash();
    window.addEventListener("hashchange", () => this.fromHash());
    document.addEventListener("keydown", (e) => this.onKey(e));
  }

  /// Deep links: `#AAPL GP` runs in panel 1, `#WEI;TOP;AAPL GP;W` fills panels.
  private fromHash(): void {
    const cmds = decodeURIComponent(location.hash.slice(1)).split(";").map((c) => c.trim()).filter(Boolean).slice(0, 4);
    if (!cmds.length) return;
    history.replaceState(null, "", location.pathname + location.search);
    if (cmds.length > this.layout) this.setLayout(cmds.length > 2 ? 4 : 2);
    cmds.forEach((c, i) => this.panels[i].run(c));
    this.focus(this.panels[0]);
  }

  setLayout(n: 1 | 2 | 4): void {
    this.layout = n;
    this.grid.className = `l${n}`;
    this.panels.forEach((p, i) => p.el.classList.toggle("hidden", i >= n));
    if (this.focused && this.focused.index >= n) this.focus(this.panels[0]);
    clear(
      this.layoutButtons,
      ...([1, 2, 4] as const).map((k) =>
        h("button", { class: k === n ? "on" : undefined, title: `${k} panel${k > 1 ? "s" : ""}`, onclick: () => this.setLayout(k) }, String(k)),
      ),
    );
    window.dispatchEvent(new Event("resize"));
    this.save();
  }

  focus(p: Panel): void {
    if (this.focused === p) return;
    this.focused?.el.classList.remove("focus");
    this.focused = p;
    p.el.classList.add("focus");
    if (document.activeElement !== p.input) p.input.focus({ preventScroll: true });
    this.save();
  }

  next(p: Panel): Panel {
    const n = this.panels[(p.index + 1) % this.layout];
    this.focus(n);
    return n;
  }

  save(): void {
    if (!this.panels.length || !this.focused) return;
    const s: Saved = { layout: this.layout, focus: this.focused.index, cmds: this.panels.map((p) => p.cmd) };
    localStorage.setItem(STORE, JSON.stringify(s));
  }

  /// Bloomberg habit: start typing anywhere and it lands on the command line.
  private onKey(e: KeyboardEvent): void {
    // e.code, not e.key: Alt+digit types symbols on macOS layouts.
    if (e.altKey && /^Digit[1-4]$/.test(e.code)) {
      e.preventDefault();
      const n = Number(e.code.slice(5));
      if (n > this.layout) this.setLayout(n > 2 ? 4 : 2);
      this.focus(this.panels[n - 1]);
      return;
    }
    if (e.altKey && e.key === "ArrowLeft") {
      e.preventDefault();
      this.focused.back();
      return;
    }
    const t = e.target as HTMLElement;
    if (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA") return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1 || e.key === "Backspace") {
      this.focused.input.focus();
      if (e.key === "Backspace" && !this.focused.input.value) {
        e.preventDefault();
        this.focused.back();
      }
    }
  }
}

const STRIP: [string, string][] = [
  ["^GSPC", "SPX"],
  ["^IXIC", "CCMP"],
  ["^DJI", "INDU"],
  ["^VIX", "VIX"],
  ["^TNX", "US10Y"],
  ["DX-Y.NYB", "DXY"],
  ["EURUSD=X", "EUR"],
  ["JPY=X", "JPY"],
  ["CL=F", "WTI"],
  ["GC=F", "GOLD"],
  ["BTC-USD", "BTC"],
];

const CLOCKS: [string, string][] = [
  ["NY", "America/New_York"],
  ["LDN", "Europe/London"],
  ["TYO", "Asia/Tokyo"],
];

function topbar(app: App): HTMLElement {
  const strip = h("div", { class: "strip" });
  const ticks = new Map<string, HTMLElement>();
  for (const [sym, label] of STRIP) {
    const el = h("span", { class: "tick", title: sym, onclick: () => app.focused.run(`${sym} GP`) }, h("span", { class: "n" }, label), "…");
    ticks.set(sym, el);
    strip.append(el);
  }
  const state = h("span", { class: "tick mstate", title: "US equity market state" });
  strip.prepend(state);
  const paint = (q: Quote) => {
    if (q.symbol === "^GSPC") {
      const s = (q.marketState ?? "").replace("PREPRE", "PRE").replace("POSTPOST", "POST");
      clear(state, h("span", { class: s === "REGULAR" ? "up" : "dim" }, s === "REGULAR" ? "US OPEN" : `US ${s || "—"}`));
    }
    const el = ticks.get(q.symbol);
    if (!el) return;
    const label = el.firstChild!;
    const dp = q.symbol.endsWith("=X") ? (q.symbol === "JPY=X" ? 2 : 4) : undefined;
    clear(el, label, f.price(q.regularMarketPrice, dp), " ", h("span", { class: f.cls(q.regularMarketChangePercent) }, f.pct(q.regularMarketChangePercent)));
  };
  const load = () =>
    quotes(STRIP.map((s) => s[0]))
      .then((r) => r.data.forEach(paint))
      .catch(() => {});
  load();
  every(30_000, load, new AbortController().signal);

  const clocks = h("div", { class: "clocks" });
  const fmts = CLOCKS.map(([label, tz]) => {
    const el = h("span", { class: "clock" });
    clocks.append(el);
    return { el, label, fmt: new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit" }) };
  });
  const tick = () => fmts.forEach((c) => clear(c.el, h("b", null, c.label), c.fmt.format(new Date())));
  tick();
  setInterval(tick, 1000);

  const banner = h("span", { class: "banner", style: { display: "none" }, title: "Click to dismiss", onclick: () => (banner.style.display = "none") });
  startAlertWatcher((text) => {
    clear(banner, text);
    banner.style.display = "";
  });

  return h("div", { id: "topbar" }, h("span", { class: "logo", title: "BERG — HELP <GO> for functions" }, "BERG"), banner, strip, clocks, app.layoutButtons);
}

new App();
